import { type Dispatch } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type CheckboxProps, Radio } from "semantic-ui-react";

import { MultiSelect } from "../../../../components/multiSelect/MultiSelect";

import { bindAction } from "../../../../interfaces";
import { type RootState } from "../../../Application/globaltypes/redux";
import { integrationBaseSelector } from "../state/selectors";
import { reset } from "../state/slices/integrationPacksSlice";
import { fetchPacksAction } from "../state/thunks/integrationPacksThunk";
import { ContentSettingsEnum, type VivaIntegrationConfigs } from "../types/viva";

import Form from "components/forms/layout";

export interface VivaIntegrationConfigurationProps extends PropsFromRedux {
  isCreating?: boolean;
  integrationName: string;
  setConfigs: (configs: VivaIntegrationConfigs) => void;
  configs: VivaIntegrationConfigs;
  accountId: number;
}

export const VivaIntegrationConfiguration: React.FC<VivaIntegrationConfigurationProps> = (props) => {
  const { integrationName, setConfigs, configs, fetchPacks, packs, isLoading, resetPacks, isCreating, accountId } =
    props;

  useEffect(() => {
    fetchPacks(accountId);
    return resetPacks;
  }, [fetchPacks, resetPacks, accountId]);

  const handleChangePacks = (data: { value: number[] }) => {
    setConfigs({
      ...configs,
      packContentSettings: {
        ...configs.packContentSettings,
        packIds: data.value,
      },
    });
  };

  const onHandlePackContentChange = (_: any, data: CheckboxProps) => {
    setConfigs({
      ...configs,
      packContentSettings: {
        ...configs.packContentSettings,
        contentType: data.value as ContentSettingsEnum,
      },
    });
  };

  return (
    <Form.Root>
      {isCreating && <Form.Block title="Integration type">{integrationName}</Form.Block>}
      <Form.Block title="Content">
        <div>
          <span>Pack selection</span>
          <MultiSelect
            items={packs}
            onMultiSelectChange={handleChangePacks}
            selected={isLoading ? [] : configs.packContentSettings.packIds}
            placeholder="Select pack(s) for more options"
            loading={isLoading}
          />
        </div>
        <Form.SubBlock>
          <Radio
            onChange={onHandlePackContentChange}
            label="Push all content in selected Packs (excludes communications)"
            checked={configs.packContentSettings.contentType === ContentSettingsEnum.AllContent}
            value={ContentSettingsEnum.AllContent}
          />
          <Radio
            onChange={onHandlePackContentChange}
            label="Push only Flows in selected Packs"
            checked={configs.packContentSettings.contentType === ContentSettingsEnum.FlowsOnly}
            value={ContentSettingsEnum.FlowsOnly}
          />
        </Form.SubBlock>
      </Form.Block>
    </Form.Root>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = integrationBaseSelector(state).base;
  return {
    isLoading: base.packs.isLoading,
    packs: base.packs.items,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchPacks: bindAction(fetchPacksAction, dispatch),
    resetPacks: bindAction(reset, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(VivaIntegrationConfiguration);
export default ConnectedComponent;
